import { AxiosResponse } from "axios";
import { apiErrorWithMessages } from "../lib/apiError";
import { Paging } from "../models/Paging";
import useApi from "../services/ApiService";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";
import { ConflictGroupDto, ConflictResolutionItemDto } from "../models/ConflictGroup";
import { ConflictReasonCount } from "../models/ConflictReasonCount";
import { IntakeFileFilters } from "../models/IntakeFileFilters";
import { Intake } from "../models/Intake";
import { IntakeFile } from "../models/IntakeFile";
import {EmptyFolder} from "../models/EmptyFolder";
import {QUERY_CONFIG} from "../constants/QueryConfig";

export interface GetIntakesQueryOptions {
  organizationId?: string;
  status?: string[] | null;
  isLocked?: boolean | null;
  createdBy?: string[];
  intakeNumber?: string[] | null;
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  hasFlaggedPaths?: boolean | null;
  hasConflicts?: boolean | null;
  /** Only entities whose flag is in one of these states. */
  flagStatus?: MigrationFlagStatus[] | null;
}
export class GetIntakeFilesForIntakeQueryOptions {
  status?: string[] | null;
  extension?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  nestedFolder1?: string | null;
  nestedFolder2?: string | null;
  filePath?: string | null;
  destinationPath?: string | null;
  minFileSize?: number | null;
  maxFileSize?: number | null;
  type?: number | null;
  failureType?: string | null;
  conflictReason?: string[] | null;
  conflictStatus?: string[] | null;
}

export class GetIntakeEmptyFoldersForIntakeQueryOptions {
  status?: string[] | null;
  uploadPathFromRoot?: string | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class IntakesRepository {
  private api = useApi();

  async getIntakes(
    options: GetIntakesQueryOptions = {}
  ): Promise<{ data: Intake[]; paging: Paging | null }> {
    const {
      status = null,
      isLocked = null,
      createdBy = null,
      intakeNumber = null,
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      hasFlaggedPaths = null,
      hasConflicts = null,
      flagStatus = null,
    } = options;

    try {
      const response = await this.api.get("/api/Intakes", {
        params: {
          Status: status,
          IsLocked: isLocked,
          CreatedBy: createdBy,
          IntakeNumber: intakeNumber,
          Id: ids,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          HasFlaggedPaths: hasFlaggedPaths,
          HasConflicts: hasConflicts,
          FlagStatus: flagStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch intakes");
    }
  }

  async getIntake(id: string): Promise<Intake> {
    try {
      const response = await this.api.get(`/api/Intakes/${id}`);
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch intake with id: ${id}`);
    }
  }

  async getIntakeFilesForIntake(
    intakeId?: string,
    options: GetIntakeFilesForIntakeQueryOptions = {}
  ): Promise<{ data: IntakeFile[]; paging: Paging | null }> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      nestedFolder1 = null,
      nestedFolder2 = null,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null
    } = options;
    try {
      const apiUrl = intakeId
        ? `/api/Intakes/${intakeId}/files`
        : "/api/IntakeFiles";
      const response = await this.api.get(apiUrl, {
        params: {
          Status: status,
          Extension: extension,
          NestedFolder1: nestedFolder1,
          NestedFolder2: nestedFolder2,
          FilePath: filePath,
          DestinationPath: destinationPath,
          MinFileSize: minFileSize,
          MaxFileSize: maxFileSize,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          Type: type,
          FailureType: failureType,
          ConflictReason: conflictReason,
          ConflictStatus: conflictStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      const message = intakeId
        ? `Failed to fetch files for intake with id: ${intakeId}`
        : `Failed to fetch intake files`;
      throw new Error(message);
    }
  }

  async getIntakeFilesFiltersForIntake(
    id: string
  ): Promise<{ data: IntakeFileFilters }> {
    try {
      const response = await this.api.get(`/api/Intakes/${id}/files/filters`, {
        timeout: 100 * 1000,
      });
      return { data: response.data };
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to fetch intake filters for intake with id: ${id}`
      );
    }
  }

  async getIntakeEmptyFoldersForIntake(
    id: string,
    options: GetIntakeEmptyFoldersForIntakeQueryOptions = {}
  ): Promise<{ data: EmptyFolder[]; paging: Paging | null }> {
    const {
      status = null,
      uploadPathFromRoot = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get(`/api/Intakes/${id}/emptyFolders`, {
        params: {
          Status: status,
          UploadPathFromRoot: uploadPathFromRoot,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: 100 * 1000,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      throw new Error(`Failed to fetch empty folder for intake with id: ${id}`);
    }
  }

  async updateIntakeFlag(
    intakeId: string,
    flagStatus: MigrationFlagStatus
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        intakeId: intakeId,
        flagStatus: flagStatus,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Intakes/updateFlag`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to update flag info of intake with id: ${intakeId}`
      );
    }
  }

  async updateIntakeBlock(intakeId: string, block: boolean): Promise<void> {
    try {
      const data: Record<string, any> = {
        entityId: intakeId,
        block: block,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Intakes/updateBlock`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to update block info of intake with id: ${intakeId}`
      );
    }
  }

  async markNotFoundFileForRetry(
    intakeId: string,
    intakeFileId: string,
    reason: string
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        migrationEntityId: intakeId,
        fileId: intakeFileId,
        reason: reason
      };
      await this.api.post(
        `/api/intakeFiles/markNotFoundForRetry`,
        data
      );
    } catch (error) {
      throw new Error(`Failed to mark not found file for retry for intakeId: ${intakeId} and intakeFileId: ${intakeFileId}`);
    }
  }

  async getConflictGroups(intakeId: string, includeResolved = false): Promise<ConflictGroupDto[]> {
    try {
      const response = await this.api.get(`/api/IntakeFiles/GetConflictGroups`, {
        params: { intakeId: intakeId, includeResolved: includeResolved },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict groups for intake with id: ${intakeId}`);
    }
  }

  async getConflictReasonCounts(): Promise<ConflictReasonCount[]> {
    try {
      const response = await this.api.get(`/api/IntakeFiles/GetConflictReasonCounts`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict reason counts for intakes`);
    }
  }

  async resolveConflictGroup(resolutions: ConflictResolutionItemDto[]): Promise<void> {
    try {
      await this.api.post(`/api/IntakeFiles/ResolveConflictGroup`, {
        resolutions: resolutions,
      });
    } catch (error) {
      throw apiErrorWithMessages(error, "Failed to resolve conflict group");
    }
  }
}

export default IntakesRepository;